/*
 * Copyright (c) 2021 Signum+ Software & Facility GmbH
 * Author: sebastian.woess@signum-plus.at
 */

import {
  Directive,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  Renderer2,
  NgZone,
  Input,
  HostBinding
} from '@angular/core';

import { ChangedEvent, TextService } from '@typescript-kit/core';
import { CheckboxModel } from '@typescript-kit/view';

import { BaseDirective } from './base.directive';

@Directive({
  selector: '[kitCheckboxInput]'
})
export class CheckboxInputDirective extends BaseDirective<CheckboxModel> implements OnDestroy {
  private unlistenInput: () => any;

  constructor(
    changeDetector: ChangeDetectorRef,
    elementRef: ElementRef,
    renderer: Renderer2,
    ngZone: NgZone,
    textService: TextService
  ) {
    super(changeDetector, elementRef, renderer, ngZone, textService);
  }

  @HostBinding('type')
  get inputType(): string {
    return 'checkbox';
  }

  get hostClass(): string {
    return 'kit-checkbox-input';
  }

  @Input()
  set kitCheckboxInput(model: CheckboxModel) {
    this.model = model;
  }

  get kitCheckboxInput(): CheckboxModel {
    return this.model;
  }

  get inputElement(): HTMLInputElement {
    return this.elementRef.nativeElement;
  }

  get focusElement(): HTMLInputElement {
    return this.inputElement;
  }

  ngOnDestroy(): void {
    this.deregisterInputHandler();
    super.ngOnDestroy();
  }

  select() {
    this.inputElement.select();
  }

  toggle() {
    this.model.checked = !this.model.checked;
  }

  protected onModelChanged(model: CheckboxModel) {
    super.onModelChanged(model);
    this.inputElement.disabled = model.isDisabled;
    this.refreshElementValue();
    this.refreshInputHandler();
  }

  protected onModelPropertyChanged(event: ChangedEvent): void {
    super.onModelPropertyChanged(event);
    if (event.changes['isDisabled']) {
      this.inputElement.disabled = this.model.isDisabled;
    }
    if (event.changes['checked']) {
      this.refreshElementValue();
    }
  }

  protected refreshModelValue() {
    if (this.model.checked !== this.inputElement.checked) {
      this.model.checked = this.inputElement.checked;
    }
  }

  protected refreshElementValue() {
    if (this.inputElement.checked !== this.model.checked) {
      this.inputElement.checked = this.model.checked;
    }
  }

  protected onEnter() {
    this.toggle();
  }

  protected onInput() {
    this.refreshModelValue();
  }

  protected refreshInputHandler() {
    this.registerInputHandler();
  }

  protected registerInputHandler() {
    if (this.unlistenInput) {
      return;
    }
    this.unlistenInput = this.renderer.listen(this.inputElement, 'input', (event: Event) => {
      this.ngZone.run(() => this.onInput());
    });
  }

  protected deregisterInputHandler() {
    if (!this.unlistenInput) {
      return;
    }
    this.unlistenInput();
    this.unlistenInput = null;
  }
}
